import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Pencil, Building2, AlertTriangle, Users as UsersIcon, Network } from "lucide-react";

// ------------------------- Create Role Modal -------------------------
function CreateRoleModal({ open, roles, branches, onClose, onSaved }) {
  const [type, setType] = useState("manager");
  const [name, setName] = useState("");
  const [reportsToRoleId, setReportsToRoleId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setType("manager");
    setName("");
    setReportsToRoleId("");
    setBranchId("");
    setErr("");
  }, [open]);

  const parentOptions = roles.filter((r) =>
    type === "executive" ? r.type === "manager" : r.type === "manager" || r.type === "director"
  );
  const directorRole = roles.find((r) => r.type === "director");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!name.trim()) return setErr("Role name required");
    if (type === "executive" && !reportsToRoleId) return setErr("Executives must report to a Manager");
    setSaving(true);
    try {
      await api.post("/roles", {
        name: name.trim(),
        type,
        reports_to_role_id: reportsToRoleId || null,
        branch_id: branchId || null,
      });
      toast.success(`Role "${name}" created`);
      onSaved();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="create-role-modal">
      <div className="w-full max-w-md iz-card p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500"><X className="w-4 h-4" /></button>
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-2">Compose</p>
        <h2 className="font-display text-2xl text-white mb-6">Create a new role</h2>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Role Type</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "manager", label: "Manager", desc: "Reports to Director (or another Manager)" },
                { v: "executive", label: "Executive", desc: "Reports to a Manager" },
              ].map((t) => (
                <button
                  type="button"
                  key={t.v}
                  onClick={() => { setType(t.v); setReportsToRoleId(""); }}
                  className={`text-left p-3 rounded-md border transition-colors ${type === t.v ? "border-[#d4af37] bg-[#d4af37]/5" : "border-[#262626] hover:border-[#3a3a3a]"}`}
                  data-testid={`role-type-${t.v}`}
                >
                  <p className={`text-sm font-medium ${type === t.v ? "text-[#d4af37]" : "text-white"}`}>{t.label}</p>
                  <p className="text-[10px] text-gray-500 mt-0.5">{t.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Role Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Analytics Manager" className="iz-input" data-testid="role-name-input" />
          </div>

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">
              Reports To {type === "manager" && <span className="text-gray-600 lowercase">(optional — defaults to Director)</span>}
            </label>
            <select value={reportsToRoleId} onChange={(e) => setReportsToRoleId(e.target.value)} className="iz-input" data-testid="role-reports-to">
              <option value="">{type === "manager" ? `Auto → ${directorRole?.name || "Director"}` : "Select manager…"}</option>
              {parentOptions.map((m) => (
                <option key={m.id} value={m.id}>{m.name} · {m.type}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">
              Branch Scope <span className="text-gray-600 lowercase">(optional)</span>
            </label>
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className="iz-input" data-testid="role-branch">
              <option value="">All branches · global</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name} · {b.city || b.type}</option>
              ))}
            </select>
          </div>

          {err && <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 rounded-md px-3 py-2">{err}</div>}

          <div className="flex justify-end gap-3 pt-4 border-t border-[#1a1a1a]">
            <button type="button" onClick={onClose} className="iz-btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="iz-btn-primary" data-testid="role-submit">
              {saving ? "Creating…" : "Create role"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ------------------------- Edit Role Modal -------------------------
function EditRoleModal({ open, role, roles, branches, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [reportsToRoleId, setReportsToRoleId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open || !role) return;
    setName(role.name || "");
    setReportsToRoleId(role.reports_to_role_id || "");
    setBranchId(role.branch_id || "");
    setErr("");
  }, [open, role]);

  if (!open || !role) return null;

  const isDirector = role.type === "director";
  const parentOptions = roles.filter((r) => {
    if (r.id === role.id) return false;
    if (role.type === "executive") return r.type === "manager" || r.type === "director";
    if (role.type === "manager") return r.type === "manager" || r.type === "director";
    return false;
  });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!name.trim()) return setErr("Role name cannot be blank");
    const payload = { name: name.trim() };
    if (!isDirector) {
      if (reportsToRoleId) {
        payload.reports_to_role_id = reportsToRoleId;
      } else {
        payload.clear_reports_to = true;  // will error backend-side for non-director; we prevent by validation.
      }
    }
    if (branchId) payload.branch_id = branchId;
    else payload.clear_branch = true;
    setSaving(true);
    try {
      await api.patch(`/roles/${role.id}`, payload);
      toast.success("Role updated");
      onSaved();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="edit-role-modal">
      <div className="w-full max-w-md iz-card p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500"><X className="w-4 h-4" /></button>
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-2">Refine</p>
        <h2 className="font-display text-2xl text-white mb-1">Edit role</h2>
        <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-6">
          {role.type} {role.is_seed && "· seed"}
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Role Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="iz-input" data-testid="edit-role-name" />
          </div>

          {!isDirector && (
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Reports To</label>
              <select value={reportsToRoleId} onChange={(e) => setReportsToRoleId(e.target.value)} className="iz-input" data-testid="edit-role-reports-to">
                <option value="">— None —</option>
                {parentOptions.map((m) => (
                  <option key={m.id} value={m.id}>{m.name} · {m.type}</option>
                ))}
              </select>
              <p className="text-[10px] text-gray-500 mt-1">Changing the parent will cascade to every user on this role.</p>
            </div>
          )}

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Branch Scope</label>
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className="iz-input" data-testid="edit-role-branch">
              <option value="">All branches · global</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name} · {b.city || b.type}</option>
              ))}
            </select>
          </div>

          {err && <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 rounded-md px-3 py-2">{err}</div>}

          <div className="flex justify-end gap-3 pt-4 border-t border-[#1a1a1a]">
            <button type="button" onClick={onClose} className="iz-btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="iz-btn-primary" data-testid="edit-role-submit">
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ------------------------- Blocking Delete Modal -------------------------
function BlockingDeleteModal({ open, detail, onClose }) {
  if (!open || !detail) return null;
  const users = detail.blocking_users || [];
  const childRoles = detail.blocking_child_roles || [];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="blocking-delete-modal">
      <div className="w-full max-w-lg iz-card p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500"><X className="w-4 h-4" /></button>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-red-500/10 border border-red-500/40 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-red-400" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-red-400">Delete blocked</p>
            <h2 className="font-display text-xl text-white mt-1">Cannot delete &ldquo;{detail.role_name}&rdquo;</h2>
            <p className="text-sm text-gray-400 mt-2">Reassign the entries below to another role, then try again.</p>
          </div>
        </div>

        {users.length > 0 && (
          <div className="mt-5 border border-[#262626] rounded-md">
            <div className="px-4 py-2 border-b border-[#262626] flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-gray-400">
              <UsersIcon className="w-3 h-3" /> {users.length} user(s) to move
            </div>
            <ul className="max-h-56 overflow-y-auto divide-y divide-[#1a1a1a]">
              {users.map((u) => (
                <li key={u.id} className="px-4 py-2.5 flex items-center justify-between" data-testid={`blocking-user-${u.id}`}>
                  <div>
                    <p className="text-sm text-white">{u.name || "—"}</p>
                    <p className="text-[11px] font-mono text-gray-500">{u.email}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {childRoles.length > 0 && (
          <div className="mt-4 border border-[#262626] rounded-md">
            <div className="px-4 py-2 border-b border-[#262626] flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-gray-400">
              <Network className="w-3 h-3" /> {childRoles.length} child role(s) to reparent
            </div>
            <ul className="max-h-40 overflow-y-auto divide-y divide-[#1a1a1a]">
              {childRoles.map((r) => (
                <li key={r.id} className="px-4 py-2.5 flex items-center justify-between" data-testid={`blocking-role-${r.id}`}>
                  <div>
                    <p className="text-sm text-white">{r.name}</p>
                    <p className="text-[11px] font-mono text-gray-500 uppercase tracking-widest">{r.type}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-5 mt-5 border-t border-[#1a1a1a]">
          <button onClick={onClose} className="iz-btn-primary" data-testid="blocking-delete-close">Got it</button>
        </div>
      </div>
    </div>
  );
}

// ------------------------- Page -------------------------
export default function RolesPage() {
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [blockDetail, setBlockDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, b] = await Promise.all([api.get("/roles"), api.get("/branches")]);
      setRoles(r.data);
      setBranches(b.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const roleById = useMemo(() => Object.fromEntries(roles.map((r) => [r.id, r])), [roles]);
  const branchById = useMemo(() => Object.fromEntries(branches.map((b) => [b.id, b])), [branches]);

  const doDelete = async (r) => {
    if (!window.confirm(`Delete role "${r.name}"? This is irreversible.`)) return;
    try {
      await api.delete(`/roles/${r.id}`);
      toast.success("Role deleted");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && (detail.blocking_users || detail.blocking_child_roles)) {
        setBlockDetail(detail);
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    }
  };

  const grouped = {
    director: roles.filter((r) => r.type === "director"),
    manager: roles.filter((r) => r.type === "manager"),
    executive: roles.filter((r) => r.type === "executive"),
  };

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-10 py-6 flex items-center justify-between bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-30">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Governance</p>
          <h1 className="font-display text-3xl text-white mt-1">Roles</h1>
        </div>
        <button className="iz-btn-primary flex items-center gap-2" onClick={() => setCreateOpen(true)} data-testid="create-role-button">
          <Plus className="w-4 h-4" strokeWidth={2} /> Create New Role
        </button>
      </div>

      <div className="px-10 py-8 space-y-8">
        {loading ? (
          <div className="text-center text-gray-500 py-20">Loading roles…</div>
        ) : (
          Object.entries(grouped).map(([type, list]) => (
            <div key={type}>
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-3">{type}s · {list.length}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {list.map((r) => {
                  const parent = r.reports_to_role_id ? roleById[r.reports_to_role_id] : null;
                  const branch = r.branch_id ? branchById[r.branch_id] : null;
                  return (
                    <div key={r.id} className="iz-card p-4 flex items-start justify-between gap-3 hover:border-[#d4af37]/40 transition-colors" data-testid={`role-card-${r.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-white font-medium truncate">{r.name}</p>
                          {r.is_seed && <span className="text-[9px] font-mono uppercase tracking-widest text-[#d4af37]/70 border border-[#d4af37]/20 rounded px-1.5 py-0.5">seed</span>}
                          {branch && (
                            <span className="text-[9px] font-mono uppercase tracking-widest text-emerald-400 border border-emerald-500/30 rounded px-1.5 py-0.5 flex items-center gap-1" data-testid={`role-branch-badge-${r.id}`}>
                              <Building2 className="w-2.5 h-2.5" /> {branch.name}
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-1.5">
                          Reports to · <span className="text-gray-400">{parent ? parent.name : (r.type === "director" ? "Self" : "—")}</span>
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => setEditRole(r)} className="p-2 rounded hover:bg-[#d4af37]/10 text-gray-500 hover:text-[#d4af37] transition-colors" title="Edit role" data-testid={`edit-role-${r.id}`}>
                          <Pencil className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                        {!r.is_seed && (
                          <button onClick={() => doDelete(r)} className="p-2 rounded hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors" title="Delete role" data-testid={`delete-role-${r.id}`}>
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      <CreateRoleModal
        open={createOpen}
        roles={roles}
        branches={branches}
        onClose={() => setCreateOpen(false)}
        onSaved={() => { setCreateOpen(false); load(); }}
      />
      <EditRoleModal
        open={!!editRole}
        role={editRole}
        roles={roles}
        branches={branches}
        onClose={() => setEditRole(null)}
        onSaved={() => { setEditRole(null); load(); }}
      />
      <BlockingDeleteModal
        open={!!blockDetail}
        detail={blockDetail}
        onClose={() => setBlockDetail(null)}
      />
    </div>
  );
}
